import AppLayout from '@cloudscape-design/components/app-layout';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SideNavigation from '@cloudscape-design/components/side-navigation';
import SpaceBetween from '@cloudscape-design/components/space-between';
import SplitPanel from '@cloudscape-design/components/split-panel';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated, userManager } from './auth/userManager.js';
import { getConfig } from './config.js';
import { SplitPanelContext, SPLIT_PANEL_I18N, type DetailPanel } from './lib/splitPanel.js';
import { NAV_ITEMS } from './nav.js';
import { VpnEnrollmentPage } from './pages/VpnEnrollmentPage.js';
import { VpnGatewayDetailPage } from './pages/VpnGatewayDetailPage.js';
import { VpnTerminalPage } from './pages/VpnTerminalPage.js';

function LoginScreen() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <SpaceBetween size="l" alignItems="center">
        <Box variant="h1">Gateway VPN Console</Box>
        <Box color="text-body-secondary">Sign in with your Viaanix Identity Center account.</Box>
        <Button variant="primary" onClick={() => void userManager()?.signinRedirect()}>
          Sign in
        </Button>
      </SpaceBetween>
    </div>
  );
}

function Shell() {
  const navigate = useNavigate();
  const location = useLocation();
  const useFixtureMode = getConfig().useFixture;
  const [detail, setDetail] = useState<DetailPanel | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const activeHref = `/${location.pathname.split('/')[1] || 'vpn'}`;

  // Tracks the previously shown panel so a content refresh for the SAME header (e.g. a poll tick
  // updating the selected row) never forces the panel back open once the user has collapsed it —
  // only a genuinely new selection (a different header, or none -> some) does that.
  const prevPanelRef = useRef<DetailPanel | null>(null);
  const showDetail = useCallback((p: DetailPanel | null) => {
    setDetail(p);
    if (p === null) {
      setPanelOpen(false);
    } else if (prevPanelRef.current === null || prevPanelRef.current.header !== p.header) {
      setPanelOpen(true);
    }
    prevPanelRef.current = p;
  }, []);

  // Close the detail panel whenever the route changes.
  useEffect(() => {
    setDetail(null);
    setPanelOpen(false);
    prevPanelRef.current = null;
  }, [location.pathname]);

  return (
    <SplitPanelContext.Provider value={{ show: showDetail }}>
      <TopNavigation
        identity={{ href: '/', title: 'Gateway VPN Console', onFollow: (e) => { e.preventDefault(); navigate('/'); } }}
        utilities={[
          {
            type: 'button',
            text: useFixtureMode ? 'demo data' : 'live',
            iconName: useFixtureMode ? 'status-info' : 'status-positive',
          },
        ]}
      />
      <AppLayout
        toolsHide
        navigation={
          <SideNavigation
            activeHref={activeHref}
            header={{ href: '/', text: 'Gateway VPN Console' }}
            items={NAV_ITEMS}
            onFollow={(e) => {
              if (!e.detail.external) {
                e.preventDefault();
                navigate(e.detail.href);
              }
            }}
          />
        }
        splitPanel={
          detail ? (
            <SplitPanel header={detail.header} i18nStrings={SPLIT_PANEL_I18N} hidePreferencesButton>
              {detail.body}
            </SplitPanel>
          ) : undefined
        }
        splitPanelOpen={panelOpen}
        onSplitPanelToggle={({ detail: d }) => setPanelOpen(d.open)}
        content={
          <Routes>
            <Route path="/" element={<Navigate to="/vpn" replace />} />
            <Route path="/vpn" element={<VpnEnrollmentPage />} />
            <Route path="/vpn/:gatewayId" element={<VpnGatewayDetailPage />} />
            <Route path="*" element={<Navigate to="/vpn" replace />} />
          </Routes>
        }
        contentType="table"
      />
    </SplitPanelContext.Provider>
  );
}

export function App() {
  const useFixtureMode = getConfig().useFixture;
  if (!useFixtureMode && !isAuthenticated()) return <LoginScreen />;
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/vpn/:gatewayId/terminal" element={<VpnTerminalPage />} />
        <Route path="*" element={<Shell />} />
      </Routes>
    </BrowserRouter>
  );
}
